import React, { useState, useEffect, useMemo, useRef } from 'react';

// =============================================================================
// PickerPopover — small type-to-filter list anchored under a clicked grid cell.
//
//   {picker && (
//     <PickerPopover
//       anchor={picker}                       // { x, y } from event.bounds
//       options={picker.options}              // [{ code, description }]
//       onPick={applyPick}                    // (code) => …
//       onClear={() => applyPick(null)}       // omit to hide the clear row
//       onClose={() => setPicker(null)}
//     />
//   )}
//
// An option is { code, label?, description?, hint? }. `code` is what onPick
// receives; it renders mono-spaced unless the option supplies a `label` to show
// instead (codes read as codes, prose reads as prose). `description` trails the
// primary text, `hint` is pinned right. Pass `value` to embolden the option that
// is currently set. Closes on Escape or a mousedown outside — no backdrop, so a
// click on another cell re-targets the picker rather than being swallowed.
// =============================================================================

const WIDTH = 300;
const MAX_HEIGHT = 330;   // input + clear row + max-h-64 list, for edge clamping

const PickerPopover = ({
  anchor,
  options = [],
  value,
  onPick,
  onClear,
  clearLabel = 'Clear',
  onClose,
  placeholder = 'Filter…',
  emptyText = 'No match.',
  limit = 200,
}) => {
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options.slice(0, limit);
    return options.filter((o) =>
      `${o.code} ${o.label || ''} ${o.description || ''}`.toLowerCase().includes(s)).slice(0, limit);
  }, [q, options, limit]);

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: Math.max(8, Math.min(anchor.x, window.innerWidth - WIDTH - 8)),
        top: Math.max(8, Math.min(anchor.y, window.innerHeight - MAX_HEIGHT)),
        zIndex: 60,
        width: WIDTH,
      }}
      className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl overflow-hidden"
    >
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 text-xs border-b border-slate-200 dark:border-slate-700 bg-transparent text-slate-700 dark:text-slate-100 outline-none"
      />
      <div className="max-h-64 overflow-y-auto">
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="w-full text-left px-2.5 py-1.5 text-xs text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            {clearLabel}
          </button>
        )}
        {shown.length === 0 && (
          <p className="m-0 px-2.5 py-3 text-xs text-slate-400">{emptyText}</p>
        )}
        {shown.map((o) => (
          <button
            key={String(o.code)}
            type="button"
            onClick={() => onPick(o.code)}
            className={`w-full flex items-baseline gap-1.5 px-2.5 py-1.5 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-700 ${
              value !== undefined && String(o.code) === String(value ?? '')
                ? 'font-semibold text-slate-900 dark:text-slate-100'
                : 'text-slate-700 dark:text-slate-200'
            }`}
          >
            {o.label
              ? <span className="truncate">{o.label}</span>
              : <span className="shrink-0 font-mono font-semibold">{o.code}</span>}
            {o.description && (
              <span className="truncate text-slate-500 dark:text-slate-400">{o.description}</span>
            )}
            {o.hint && <span className="ml-auto shrink-0 text-slate-400">{o.hint}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PickerPopover;
